import {
    SimpleReelModel,
    SimpleReelModelDelegate,
    SimpleReelModelState,
    SimpleReelModelSymbolData
} from "../../core/reel/simplereel/SimpleReelModel";
import {KennethMustDie} from "../KennethMustDie";
import {KMD_SYMBOL_ANIMATIONS, SPECIAL_SYMBOL_NAMES} from "../server/IKMDData";
import {KMDSymbol} from "../reels/KMDSymbol";
import {KMDSound} from "../sound/KMDSound";
import {gsap} from "gsap";

export class KMDSpecialReelModel extends SimpleReelModel {

    constructor(id: number, delegate: SimpleReelModelDelegate) {
        super(id, delegate);

        this.paddingConfig = {
            visualPaddingBefore: 0,
            paddingBefore: 0,
            visibleSymbols: 3,
            paddingAfter: 0,
            visualPaddingAfter: 0
        };

        this._direction = "horizontal";
    }

    public setInteractiveSymbols(bool: boolean): void {
        const mode: string = KennethMustDie.model.data.mode;

        // if any of the mode is FS
        if (mode.indexOf("FREESPIN") > -1) {
            return;
        }

        for (let ctr: number = 0; ctr < this.stopSymbols.length; ctr++) {
            const sym: SimpleReelModelSymbolData = this.stopSymbols[ctr],
                reelPosition: number = sym.reelPosition;

            if (KennethMustDie.model.gameData && (KennethMustDie.model.gameData.evaluatedArea[reelPosition + 1][3] === SPECIAL_SYMBOL_NAMES.NW)) {
                continue;
            }

            const symbol: KMDSymbol = (sym.positionable as KMDSymbol);
            if (KennethMustDie.model.initData.symbolValues[symbol.name] && (reelPosition > -1 && reelPosition < 4)) {
                symbol.setInteractive(bool);
            }
        }
    }

    public stopIdxWise(idx: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    if (idx === 2) {
                        this.state = SimpleReelModelState.STOPPED;
                        this.state = SimpleReelModelState.IDLE;
                    }
                }
            }),
            duration: number = 0.1,
            symbol: SimpleReelModelSymbolData = this.stopSymbols[idx],
            sym: KMDSymbol = (symbol.positionable as KMDSymbol),
            multiValue: number = KennethMustDie.model.gameData.multiplier[idx + 1][3];
        let delay: number = 0;

        tl.to(sym.position, {
            duration: duration,
            delay: delay,
            x: (this.stepSize * symbol.reelPosition),
            ease: "none"
        });

        tl.add([
            KennethMustDie.multipliersChain.jumpBall(idx),
            () => {
                this.handleStopSounds(sym, multiValue);

                if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_WAYS || sym.originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
                    KennethMustDie.winOverlays.playLightSweepFrameHighlight(idx, 3);
                }
            }
        ]);

        if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_WAYS) {
            tl.add(() => {
                sym.showSymbolCount(multiValue);
            });
        } else if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
            tl.add(sym.countMultiplierOnXNudgeInSpecialReel(multiValue));
        } else {
            sym.resizeLevel = 0;

            const state: string = KMD_SYMBOL_ANIMATIONS.NORMAL + (multiValue > 1 ? Math.min(multiValue, sym.maxSplitParts) : "");

            tl.add([
                () => {
                    sym.setSplitState(multiValue);
                },
                sym.displayLines(multiValue),
                KennethMustDie.winOverlays.playLightSweepAnimation(state, idx, 3)
            ], "-=0.1");
        }

        return tl;
    }

    public getFillUpAnimationIdxWise(idx: number): gsap.core.Timeline {
        const fillUpTimeline: gsap.core.Timeline = gsap.timeline();
        let timeline: gsap.core.Timeline;

        if (this.avalancheSymbolsRemoved === 0) {
            return fillUpTimeline;
        }
        const duration: number = 0.2,
            symbol: SimpleReelModelSymbolData = this.stopSymbols[idx];
        if (!symbol) {
            throw new Error(`getFillUpAnimation, symbol is null!`);
        } else {
            const sym: KMDSymbol = (symbol.positionable as KMDSymbol);
            if (sym.position.x !== (this.stepSize * symbol.reelPosition)) {
                const multiValue: number = KennethMustDie.model.gameData.multiplier[idx + 1][3];

                timeline = gsap.timeline({
                    delay: 0.1 * idx
                });

                timeline.to(sym.position, {
                    duration: duration,
                    x: (this.stepSize * symbol.reelPosition),
                    ease: "none"
                });

                timeline.add([
                    KennethMustDie.multipliersChain.jumpBall(idx),
                    () => {
                        this.handleStopSounds(sym, multiValue);

                        if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_WAYS || sym.originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
                            KennethMustDie.winOverlays.playLightSweepFrameHighlight(idx, 3);
                        }
                    }
                ]);

                if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_WAYS) {
                    timeline.add(() => {
                        sym.showSymbolCount(multiValue);
                    });
                } else if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
                    timeline.add(sym.countMultiplierOnXNudgeInSpecialReel(multiValue));
                } else {
                    sym.resizeLevel = 0;

                    const state: string = KMD_SYMBOL_ANIMATIONS.NORMAL + (multiValue > 1 ? Math.min(multiValue, sym.maxSplitParts) : "");

                    timeline.add([
                        () => {
                            sym.setSplitState(multiValue);
                        },
                        sym.displayLines(multiValue),
                        KennethMustDie.winOverlays.playLightSweepAnimation(state, idx, 3)
                    ], "-=0.1");
                }

                fillUpTimeline.add(timeline);
            }
        }

        return fillUpTimeline;
    }

    // #226, #240, #237
    private handleStopSounds(sym: KMDSymbol, multiValue: number): void {
        // #226, #240, #237
        if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
            KennethMustDie.soundPlayer.playSound(KMDSound.X_NUDGE_LAND);

            // #442
            if (multiValue > 1) {
                KennethMustDie.soundPlayer.playSound(KMDSound.BASEMENT_REEL_MULTI_START);
            }
        } else if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_WAYS) {
            KennethMustDie.soundPlayer.playSound(KMDSound.X_WAYS_LAND);

            // #442
            if (multiValue > 1) {
                KennethMustDie.soundPlayer.playSound(KMDSound.BASEMENT_REEL_MULTI_START);
            }
        } else {
            KennethMustDie.soundPlayer.playSound(KMDSound.BASEMENT_REEL_STOP);

            // #442
            if (multiValue > 1) {
                KennethMustDie.soundPlayer.playSound(KMDSound.BASEMENT_REEL_SPLIT_SYMBOL);
            }
        }
    }
}